import { useEffect, useState, useRef } from 'react';
import {
  Plus,
  Building2,
  Trash2,
  Edit,
  DollarSign,
  Receipt,
  Loader2,
  Upload,
  FileSpreadsheet,
  Check,
  X,
  List,
  Download,
} from 'lucide-react';
import { useTaxStore } from '@/store/taxStore';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';

const entityTypes = [
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'llc', label: 'LLC' },
  { value: 'llc_single_member', label: 'LLC (Single Member)' },
  { value: 'llc_multi_member', label: 'LLC (Multi Member)' },
  { value: 'llc_s_corp', label: 'LLC (S-Corp Election)' },
  { value: 's_corp', label: 'S-Corporation' },
  { value: 'c_corp', label: 'C-Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'non_profit', label: 'Non-Profit' },
];

const expenseCategories = [
  { value: 'advertising', label: 'Advertising' },
  { value: 'car_and_truck', label: 'Car & Truck Expenses' },
  { value: 'commissions', label: 'Commissions' },
  { value: 'contract_labor', label: 'Contract Labor' },
  { value: 'depreciation', label: 'Depreciation' },
  { value: 'employee_benefits', label: 'Employee Benefits' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'interest_mortgage', label: 'Interest (Mortgage)' },
  { value: 'interest_other', label: 'Interest (Other)' },
  { value: 'legal_professional', label: 'Legal & Professional Services' },
  { value: 'office_expense', label: 'Office Expense' },
  { value: 'rent_lease_property', label: 'Rent/Lease (Property)' },
  { value: 'rent_lease_equipment', label: 'Rent/Lease (Equipment)' },
  { value: 'repairs_maintenance', label: 'Repairs & Maintenance' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'taxes_licenses', label: 'Taxes & Licenses' },
  { value: 'travel', label: 'Travel' },
  { value: 'meals', label: 'Meals' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'wages', label: 'Wages' },
  { value: 'startup_costs', label: 'Startup Costs' },
  { value: 'software_subscriptions', label: 'Software & Subscriptions' },
  { value: 'equipment_purchase', label: 'Equipment Purchase' },
  { value: 'home_office', label: 'Home Office' },
  { value: 'other', label: 'Other' },
];

export function BusinessesPage() {
  const { selectedYear, businesses, fetchBusinesses, isLoading } = useTaxStore();
  const [showModal, setShowModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    entityType: 'llc_single_member',
    ein: '',
    industry: '',
  });
  const [saving, setSaving] = useState(false);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState<'income' | 'expenses'>('income');
  const [importBusinessId, setImportBusinessId] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    type: 'success' | 'error';
    message: string;
    details?: any;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // CSV Preview state
  const [parsedCsvData, setParsedCsvData] = useState<any[]>([]);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);

  // Manual entry state
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [expenseData, setExpenseData] = useState({
    category: 'other',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
  });
  const [incomeData, setIncomeData] = useState({
    source: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    clientName: '',
  });
  const [savingExpense, setSavingExpense] = useState(false);
  const [savingIncome, setSavingIncome] = useState(false);

  // Records view state
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [recordsType, setRecordsType] = useState<'income' | 'expenses'>('income');
  const [recordsBusinessId, setRecordsBusinessId] = useState('');
  const [recordsBusinessName, setRecordsBusinessName] = useState('');
  const [records, setRecords] = useState<any[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [editRecordData, setEditRecordData] = useState<any>({});
  const [savingRecord, setSavingRecord] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);

  const handleExportCsv = async (businessId: string, businessName: string) => {
    setExportingId(businessId);
    try {
      const [expensesResult, incomeResult] = await Promise.all([
        api.listExpenses({ businessId, year: selectedYear }),
        api.listIncome({ businessId, year: selectedYear }),
      ]);

      const csvLines: string[] = [];

      // Income section
      csvLines.push('INCOME');
      csvLines.push('Date,Source,Description,Amount,Client,Invoice Number');
      (incomeResult.income || []).forEach((inc: any) => {
        csvLines.push(
          [
            inc.date,
            `"${(inc.source || '').replace(/"/g, '""')}"`,
            `"${(inc.description || '').replace(/"/g, '""')}"`,
            inc.amount,
            `"${(inc.clientName || '').replace(/"/g, '""')}"`,
            `"${(inc.invoiceNumber || '').replace(/"/g, '""')}"`,
          ].join(',')
        );
      });

      csvLines.push('');

      // Expenses section
      csvLines.push('EXPENSES');
      csvLines.push('Date,Category,Description,Amount,Vendor,Deductible,Deductible %');
      (expensesResult.expenses || []).forEach((exp: any) => {
        const categoryLabel = expenseCategories.find((c) => c.value === exp.category)?.label || exp.category;
        csvLines.push(
          [
            exp.date,
            `"${categoryLabel.replace(/"/g, '""')}"`,
            `"${(exp.description || '').replace(/"/g, '""')}"`,
            exp.amount,
            `"${(exp.vendor || '').replace(/"/g, '""')}"`,
            exp.isDeductible ? 'Yes' : 'No',
            exp.deductiblePercent ?? 100,
          ].join(',')
        );
      });

      const csvContent = csvLines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${businessName.replace(/[^a-zA-Z0-9]/g, '_')}_${selectedYear}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
    setExportingId(null);
  };

  useEffect(() => {
    fetchBusinesses(selectedYear);
  }, [fetchBusinesses, selectedYear]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingBusiness) {
        await api.updateBusiness(editingBusiness.id, formData);
      } else {
        await api.createBusiness(formData);
      }
      fetchBusinesses(selectedYear);
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error('Error saving business:', err);
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure? This will delete all associated expenses and income.')) return;
    try {
      await api.deleteBusiness(id);
      fetchBusinesses(selectedYear);
    } catch (err) {
      console.error('Error deleting business:', err);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', entityType: 'llc_single_member', ein: '', industry: '' });
    setEditingBusiness(null);
  };

  const openEdit = (business: any) => {
    setFormData({
      name: business.name,
      entityType: business.entityType,
      ein: business.ein || '',
      industry: business.industry || '',
    });
    setEditingBusiness(business);
    setShowModal(true);
  };

  const parseCsv = (content: string): any[] => {
    const lines = content.trim().split('\n');
    if (lines.length < 2) return [];

    // Parse header
    const headerLine = lines[0];
    const headers = headerLine.split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());

    // Parse data rows
    const data: any[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Handle quoted CSV values
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const row: any = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });

      // Parse amount as number
      if (row.amount) {
        row.amount = parseFloat(row.amount) || 0;
      }

      data.push(row);
    }

    return data;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setCsvContent(content);
        const parsed = parseCsv(content);
        setParsedCsvData(parsed);
        if (parsed.length > 0) {
          setShowCsvPreview(true);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleRemoveCsvRow = (index: number) => {
    setParsedCsvData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateCsvRow = (index: number, field: string, value: string | number) => {
    setParsedCsvData((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const regenerateCsvContent = () => {
    if (parsedCsvData.length === 0) return '';

    const headers = Object.keys(parsedCsvData[0]);
    const headerLine = headers.join(',');
    const dataLines = parsedCsvData.map((row) =>
      headers.map((h) => {
        const val = row[h];
        // Quote strings that contain commas
        if (typeof val === 'string' && val.includes(',')) {
          return `"${val}"`;
        }
        return val;
      }).join(',')
    );

    return [headerLine, ...dataLines].join('\n');
  };

  const handleImport = async () => {
    if (!importBusinessId || parsedCsvData.length === 0) {
      setImportResult({ type: 'error', message: 'Please select a business and upload a CSV file with data' });
      return;
    }

    setImporting(true);
    setImportResult(null);

    // Regenerate CSV from potentially edited data
    const finalCsvContent = regenerateCsvContent();

    try {
      if (importType === 'income') {
        const result = await api.importIncome({
          businessId: importBusinessId,
          year: selectedYear,
          csvData: finalCsvContent,
        });

        setImportResult({
          type: 'success',
          message: `Successfully imported ${result.imported} income records totaling ${formatCurrency(result.totalAmount)}`,
          details: result.records,
        });
      } else {
        const result = await api.importExpenses({
          businessId: importBusinessId,
          year: selectedYear,
          csvData: finalCsvContent,
        });

        setImportResult({
          type: 'success',
          message: `Successfully imported ${result.imported} expense records totaling ${formatCurrency(result.totalAmount)}`,
          details: result.records,
        });
      }

      fetchBusinesses(selectedYear); // Refresh to show new totals
      setShowCsvPreview(false);
    } catch (err: any) {
      setImportResult({
        type: 'error',
        message: err.message || `Failed to import ${importType}`,
      });
    }

    setImporting(false);
  };

  const resetImport = () => {
    setShowImportModal(false);
    setImportBusinessId('');
    setCsvContent('');
    setImportResult(null);
    setParsedCsvData([]);
    setShowCsvPreview(false);
    setEditingRowIndex(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openExpenseModal = (businessId: string) => {
    setSelectedBusinessId(businessId);
    setExpenseData({
      category: 'other',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      vendor: '',
    });
    setShowExpenseModal(true);
  };

  const openIncomeModal = (businessId: string) => {
    setSelectedBusinessId(businessId);
    setIncomeData({
      source: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      clientName: '',
    });
    setShowIncomeModal(true);
  };

  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingExpense(true);
    try {
      await api.addExpense({
        businessId: selectedBusinessId,
        year: selectedYear,
        category: expenseData.category,
        description: expenseData.description,
        amount: parseFloat(expenseData.amount),
        date: expenseData.date,
        vendor: expenseData.vendor || undefined,
      });
      fetchBusinesses(selectedYear);
      setShowExpenseModal(false);
    } catch (err) {
      console.error('Error adding expense:', err);
    }
    setSavingExpense(false);
  };

  const handleAddIncome = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingIncome(true);
    try {
      await api.addIncome({
        businessId: selectedBusinessId,
        year: selectedYear,
        source: incomeData.source,
        description: incomeData.description,
        amount: parseFloat(incomeData.amount),
        date: incomeData.date,
        clientName: incomeData.clientName || undefined,
      });
      fetchBusinesses(selectedYear);
      setShowIncomeModal(false);
    } catch (err) {
      console.error('Error adding income:', err);
    }
    setSavingIncome(false);
  };

  const openRecordsModal = async (businessId: string, businessName: string, type: 'income' | 'expenses') => {
    setRecordsBusinessId(businessId);
    setRecordsBusinessName(businessName);
    setRecordsType(type);
    setShowRecordsModal(true);
    setLoadingRecords(true);
    setRecords([]);

    try {
      if (type === 'income') {
        const result = await api.listIncome({ businessId, year: selectedYear });
        setRecords(result.income || []);
      } else {
        const result = await api.listExpenses({ businessId, year: selectedYear });
        setRecords(result.expenses || []);
      }
    } catch (err) {
      console.error('Error loading records:', err);
    }
    setLoadingRecords(false);
  };

  const handleDeleteRecord = async (id: string) => {
    if (!confirm('Are you sure you want to delete this record?')) return;
    setDeletingId(id);
    try {
      if (recordsType === 'income') {
        await api.deleteIncome(id);
      } else {
        await api.deleteExpense(id);
      }
      setRecords(records.filter((r) => r.id !== id));
      fetchBusinesses(selectedYear); // Refresh totals
    } catch (err) {
      console.error('Error deleting record:', err);
    }
    setDeletingId(null);
  };

  const startEditingRecord = (record: any) => {
    setEditingRecordId(record.id);
    setEditRecordData({ ...record });
  };

  const cancelEditingRecord = () => {
    setEditingRecordId(null);
    setEditRecordData({});
  };

  const handleSaveRecord = async () => {
    if (!editingRecordId) return;
    setSavingRecord(true);
    try {
      if (recordsType === 'income') {
        await api.updateIncome(editingRecordId, {
          source: editRecordData.source,
          description: editRecordData.description,
          amount: parseFloat(editRecordData.amount),
          date: editRecordData.date,
          clientName: editRecordData.clientName,
        });
      } else {
        await api.updateExpense(editingRecordId, {
          category: editRecordData.category,
          description: editRecordData.description,
          amount: parseFloat(editRecordData.amount),
          date: editRecordData.date,
          vendor: editRecordData.vendor,
        });
      }
      // Update local records
      setRecords(records.map((r) => (r.id === editingRecordId ? { ...r, ...editRecordData, amount: parseFloat(editRecordData.amount) } : r)));
      setEditingRecordId(null);
      setEditRecordData({});
      fetchBusinesses(selectedYear); // Refresh totals
    } catch (err) {
      console.error('Error updating record:', err);
    }
    setSavingRecord(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Your Businesses</h2>
          <p className="text-muted-foreground">Track income and expenses for each entity</p>
        </div>
        <div className="flex gap-2">
          {businesses.length > 0 && (
            <>
              <button
                onClick={() => {
                  setImportType('expenses');
                  setShowImportModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-smooth"
              >
                <Upload className="h-4 w-4" />
                Import Expenses
              </button>
              <button
                onClick={() => {
                  setImportType('income');
                  setShowImportModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-smooth"
              >
                <Upload className="h-4 w-4" />
                Import Income
              </button>
            </>
          )}
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
          >
            <Plus className="h-4 w-4" />
            Add Business
          </button>
        </div>
      </div>

      {/* Business Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      ) : businesses.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No businesses yet</h3>
          <p className="text-muted-foreground mb-4">
            Add your businesses to track Schedule C income and expenses
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
          >
            Add Your First Business
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {businesses.map((business) => (
            <div
              key={business.id}
              className="bg-card border border-border rounded-xl p-5 card-hover"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{business.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {entityTypes.find((t) => t.value === business.entityType)?.label}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleExportCsv(business.id, business.name)}
                    disabled={exportingId === business.id}
                    className="p-1.5 rounded-lg hover:bg-accent transition-smooth"
                    title="Export CSV"
                  >
                    {exportingId === business.id ? (
                      <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                  <button
                    onClick={() => openEdit(business)}
                    className="p-1.5 rounded-lg hover:bg-accent transition-smooth"
                  >
                    <Edit className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => handleDelete(business.id)}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 transition-smooth"
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              </div>

              {business.ein && (
                <p className="text-sm text-muted-foreground mb-3">EIN: {business.ein}</p>
              )}

              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
                <div className="text-center p-2 rounded-lg bg-green-500/10">
                  <button
                    onClick={() => openRecordsModal(business.id, business.name, 'income')}
                    className="w-full hover:opacity-80 transition-smooth"
                  >
                    <DollarSign className="h-4 w-4 mx-auto mb-1 text-green-500" />
                    <p className="text-xs text-muted-foreground">Income</p>
                    <p className="font-semibold text-green-500">
                      {formatCurrency(business.financials?.totalIncome || 0)}
                    </p>
                  </button>
                  <button
                    onClick={() => openIncomeModal(business.id)}
                    className="text-xs text-green-600 mt-1 hover:underline"
                  >
                    + Add
                  </button>
                </div>
                <div className="text-center p-2 rounded-lg bg-red-500/10">
                  <button
                    onClick={() => openRecordsModal(business.id, business.name, 'expenses')}
                    className="w-full hover:opacity-80 transition-smooth"
                  >
                    <Receipt className="h-4 w-4 mx-auto mb-1 text-red-500" />
                    <p className="text-xs text-muted-foreground">Expenses</p>
                    <p className="font-semibold text-red-500">
                      {formatCurrency(business.financials?.totalExpenses || 0)}
                    </p>
                  </button>
                  <button
                    onClick={() => openExpenseModal(business.id)}
                    className="text-xs text-red-600 mt-1 hover:underline"
                  >
                    + Add
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4">
              {editingBusiness ? 'Edit Business' : 'Add New Business'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Business Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., My Consulting LLC"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Entity Type</label>
                <select
                  value={formData.entityType}
                  onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {entityTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">EIN (optional)</label>
                <input
                  type="text"
                  value={formData.ein}
                  onChange={(e) => setFormData({ ...formData, ein: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="XX-XXXXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Industry (optional)</label>
                <input
                  type="text"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Technology Consulting"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  ) : editingBusiness ? (
                    'Save Changes'
                  ) : (
                    'Add Business'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Income Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <FileSpreadsheet className={`h-6 w-6 ${importType === 'income' ? 'text-green-500' : 'text-red-500'}`} />
              <h3 className="text-lg font-semibold">
                Import {importType === 'income' ? 'Income' : 'Expenses'} from CSV
              </h3>
            </div>

            {importResult && (
              <div
                className={`p-3 rounded-lg mb-4 ${
                  importResult.type === 'success'
                    ? 'bg-green-500/10 text-green-500'
                    : 'bg-destructive/10 text-destructive'
                }`}
              >
                <div className="flex items-center gap-2">
                  {importResult.type === 'success' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                  <span className="font-medium">{importResult.message}</span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Business</label>
                <select
                  value={importBusinessId}
                  onChange={(e) => setImportBusinessId(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Choose a business...</option>
                  {businesses.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Upload CSV File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-primary file:text-primary-foreground file:font-medium file:cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {importType === 'income'
                    ? 'CSV should have columns: date, client, description, amount'
                    : 'CSV should have columns: date, category, description, amount, vendor (optional)'}
                </p>
              </div>

              {csvContent && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    File loaded: {csvContent.split('\n').length - 1} data rows detected
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetImport}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
                >
                  {importResult?.type === 'success' ? 'Done' : 'Cancel'}
                </button>
                {importResult?.type !== 'success' && (
                  <button
                    onClick={handleImport}
                    disabled={importing || !importBusinessId || !csvContent}
                    className={`flex-1 px-4 py-2 text-white rounded-lg font-medium transition-smooth disabled:opacity-50 disabled:cursor-not-allowed ${
                      importType === 'income'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {importing ? (
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    ) : importType === 'income' ? (
                      'Import Income'
                    ) : (
                      'Import Expenses'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Receipt className="h-6 w-6 text-red-500" />
              <h3 className="text-lg font-semibold">Add Expense</h3>
            </div>

            <form onSubmit={handleAddExpense} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={expenseData.category}
                  onChange={(e) => setExpenseData({ ...expenseData, category: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                >
                  {expenseCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={expenseData.description}
                  onChange={(e) => setExpenseData({ ...expenseData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Office supplies from Staples"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-2">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={expenseData.amount}
                    onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date</label>
                  <input
                    type="date"
                    value={expenseData.date}
                    onChange={(e) => setExpenseData({ ...expenseData, date: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Vendor (optional)</label>
                <input
                  type="text"
                  value={expenseData.vendor}
                  onChange={(e) => setExpenseData({ ...expenseData, vendor: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Amazon, Staples"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowExpenseModal(false)}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingExpense}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-smooth disabled:opacity-50"
                >
                  {savingExpense ? (
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  ) : (
                    'Add Expense'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Income Modal */}
      {showIncomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="h-6 w-6 text-green-500" />
              <h3 className="text-lg font-semibold">Add Income</h3>
            </div>

            <form onSubmit={handleAddIncome} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Source</label>
                <input
                  type="text"
                  value={incomeData.source}
                  onChange={(e) => setIncomeData({ ...incomeData, source: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Consulting, Services"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={incomeData.description}
                  onChange={(e) => setIncomeData({ ...incomeData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Website development project"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-2">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={incomeData.amount}
                    onChange={(e) => setIncomeData({ ...incomeData, amount: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date</label>
                  <input
                    type="date"
                    value={incomeData.date}
                    onChange={(e) => setIncomeData({ ...incomeData, date: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Client Name (optional)</label>
                <input
                  type="text"
                  value={incomeData.clientName}
                  onChange={(e) => setIncomeData({ ...incomeData, clientName: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Acme Corp"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowIncomeModal(false)}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingIncome}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-smooth disabled:opacity-50"
                >
                  {savingIncome ? (
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  ) : (
                    'Add Income'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Records Modal */}
      {showRecordsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-2xl mx-4 shadow-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {recordsType === 'income' ? (
                  <DollarSign className="h-6 w-6 text-green-500" />
                ) : (
                  <Receipt className="h-6 w-6 text-red-500" />
                )}
                <div>
                  <h3 className="text-lg font-semibold">
                    {recordsType === 'income' ? 'Income' : 'Expenses'} - {recordsBusinessName}
                  </h3>
                  <p className="text-sm text-muted-foreground">{selectedYear}</p>
                </div>
              </div>
              <button
                onClick={() => setShowRecordsModal(false)}
                className="p-1.5 rounded-lg hover:bg-accent transition-smooth"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {loadingRecords ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
              ) : records.length === 0 ? (
                <div className="text-center py-12">
                  <List className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No {recordsType} records found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {records.map((record) => (
                    <div key={record.id} className="p-3 bg-muted/50 rounded-lg">
                      {editingRecordId === record.id ? (
                        // Edit mode
                        <div className="space-y-3">
                          {recordsType === 'expenses' ? (
                            <>
                              <div className="grid grid-cols-2 gap-2">
                                <select
                                  value={editRecordData.category || ''}
                                  onChange={(e) => setEditRecordData({ ...editRecordData, category: e.target.value })}
                                  className="px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                  {expenseCategories.map((cat) => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                  ))}
                                </select>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editRecordData.amount || ''}
                                  onChange={(e) => setEditRecordData({ ...editRecordData, amount: e.target.value })}
                                  className="px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                  placeholder="Amount"
                                />
                              </div>
                              <input
                                type="text"
                                value={editRecordData.description || ''}
                                onChange={(e) => setEditRecordData({ ...editRecordData, description: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Description"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="date"
                                  value={editRecordData.date || ''}
                                  onChange={(e) => setEditRecordData({ ...editRecordData, date: e.target.value })}
                                  className="px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                                <input
                                  type="text"
                                  value={editRecordData.vendor || ''}
                                  onChange={(e) => setEditRecordData({ ...editRecordData, vendor: e.target.value })}
                                  className="px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                  placeholder="Vendor"
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="text"
                                  value={editRecordData.source || ''}
                                  onChange={(e) => setEditRecordData({ ...editRecordData, source: e.target.value })}
                                  className="px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                  placeholder="Source"
                                />
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editRecordData.amount || ''}
                                  onChange={(e) => setEditRecordData({ ...editRecordData, amount: e.target.value })}
                                  className="px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                  placeholder="Amount"
                                />
                              </div>
                              <input
                                type="text"
                                value={editRecordData.description || ''}
                                onChange={(e) => setEditRecordData({ ...editRecordData, description: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Description"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="date"
                                  value={editRecordData.date || ''}
                                  onChange={(e) => setEditRecordData({ ...editRecordData, date: e.target.value })}
                                  className="px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                                <input
                                  type="text"
                                  value={editRecordData.clientName || ''}
                                  onChange={(e) => setEditRecordData({ ...editRecordData, clientName: e.target.value })}
                                  className="px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                  placeholder="Client Name"
                                />
                              </div>
                            </>
                          )}
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={cancelEditingRecord}
                              className="px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-smooth"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveRecord}
                              disabled={savingRecord}
                              className={`px-3 py-1.5 text-sm text-white rounded-lg transition-smooth disabled:opacity-50 ${
                                recordsType === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                              }`}
                            >
                              {savingRecord ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">
                                {recordsType === 'income'
                                  ? record.source || record.clientName || 'Income'
                                  : expenseCategories.find((c) => c.value === record.category)?.label || record.category}
                              </p>
                              <span className={`font-semibold ${recordsType === 'income' ? 'text-green-500' : 'text-red-500'}`}>
                                {formatCurrency(record.amount)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {record.description}
                              {record.vendor && ` • ${record.vendor}`}
                              {record.clientName && recordsType === 'income' && ` • ${record.clientName}`}
                            </p>
                            <p className="text-xs text-muted-foreground">{record.date}</p>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <button
                              onClick={() => startEditingRecord(record)}
                              className="p-2 rounded-lg hover:bg-accent transition-smooth"
                            >
                              <Edit className="h-4 w-4 text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => handleDeleteRecord(record.id)}
                              disabled={deletingId === record.id}
                              className="p-2 rounded-lg hover:bg-destructive/10 transition-smooth"
                            >
                              {deletingId === record.id ? (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              ) : (
                                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4 mt-4 border-t border-border">
              <button
                onClick={() => setShowRecordsModal(false)}
                className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowRecordsModal(false);
                  if (recordsType === 'income') {
                    openIncomeModal(recordsBusinessId);
                  } else {
                    openExpenseModal(recordsBusinessId);
                  }
                }}
                className={`flex-1 px-4 py-2 text-white rounded-lg font-medium transition-smooth ${
                  recordsType === 'income'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                + Add {recordsType === 'income' ? 'Income' : 'Expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
